import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CartLine } from "../lib/types";
import { round3 } from "../lib/pricing";
import { validatePromo, type PromoResult } from "../data/promos";
import { lineUid } from "../data/modifiers";

interface CartCtx {
  lines: CartLine[];
  count: number;
  subtotal: number;
  /** Promo discount off the subtotal (never touches delivery). */
  discount: number;
  /** subtotal − discount, floored at 0. */
  discountedSubtotal: number;
  /** The applied code, or null. */
  promoCode: string | null;
  /** True when a stored code no longer applies (e.g. cart dropped below min). */
  promoInvalid: boolean;
  /** By line uid (equals itemId for plain, option-less items). */
  qtyOf: (uid: string) => number;
  add: (line: Omit<CartLine, "qty" | "uid">, qty?: number) => void;
  setQty: (uid: string, qty: number) => void;
  remove: (uid: string) => void;
  applyPromo: (code: string) => PromoResult;
  clearPromo: () => void;
  clear: () => void;
}

const Ctx = createContext<CartCtx | null>(null);
const STORAGE_KEY = "kb_cart";
const PROMO_KEY = "kb_promo";

function load(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? (JSON.parse(raw) as CartLine[]) : [];
    // Backfill uid for carts saved before the modifiers engine existed.
    return arr.map((l) => ({
      ...l,
      uid: l.uid ?? lineUid(l.itemId, l.modifiers, l.notes),
    }));
  } catch {
    return [];
  }
}

function loadPromo(): string | null {
  try {
    return localStorage.getItem(PROMO_KEY);
  } catch {
    return null;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(load);
  const [promoCode, setPromoCode] = useState<string | null>(loadPromo);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  useEffect(() => {
    if (promoCode) localStorage.setItem(PROMO_KEY, promoCode);
    else localStorage.removeItem(PROMO_KEY);
  }, [promoCode]);

  const add = useCallback((line: Omit<CartLine, "qty" | "uid">, qty = 1) => {
    const uid = lineUid(line.itemId, line.modifiers, line.notes);
    setLines((prev) => {
      const existing = prev.find((l) => l.uid === uid);
      if (existing) {
        return prev.map((l) => (l.uid === uid ? { ...l, qty: l.qty + qty } : l));
      }
      return [...prev, { ...line, uid, qty }];
    });
  }, []);

  const setQty = useCallback((uid: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.uid !== uid)
        : prev.map((l) => (l.uid === uid ? { ...l, qty } : l)),
    );
  }, []);

  const remove = useCallback((uid: string) => {
    setLines((prev) => prev.filter((l) => l.uid !== uid));
  }, []);

  const clear = useCallback(() => {
    setLines([]);
    setPromoCode(null);
  }, []);

  const subtotal = useMemo(
    () => round3(lines.reduce((s, l) => s + l.price * l.qty, 0)),
    [lines],
  );

  // Re-validate the stored code against the live subtotal every render, so a
  // discount recalculates when the cart changes and disappears if it no longer
  // qualifies (e.g. items removed below the code's minimum).
  const promoResult = useMemo<PromoResult | null>(
    () => (promoCode ? validatePromo(promoCode, subtotal) : null),
    [promoCode, subtotal],
  );
  const discount = promoResult?.ok ? promoResult.discount : 0;

  const applyPromo = useCallback(
    (code: string): PromoResult => {
      const res = validatePromo(code, subtotal);
      if (res.ok) setPromoCode(res.promo.code);
      return res;
    },
    [subtotal],
  );

  const clearPromo = useCallback(() => setPromoCode(null), []);

  const value = useMemo<CartCtx>(() => {
    const count = lines.reduce((s, l) => s + l.qty, 0);
    return {
      lines,
      count,
      subtotal,
      discount,
      discountedSubtotal: round3(Math.max(0, subtotal - discount)),
      promoCode: promoResult?.ok ? promoCode : promoCode, // keep raw code for display
      promoInvalid: Boolean(promoCode) && !promoResult?.ok,
      qtyOf: (uid) => lines.find((l) => l.uid === uid)?.qty ?? 0,
      add,
      setQty,
      remove,
      applyPromo,
      clearPromo,
      clear,
    };
  }, [
    lines,
    subtotal,
    discount,
    promoCode,
    promoResult,
    add,
    setQty,
    remove,
    applyPromo,
    clearPromo,
    clear,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart(): CartCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
